import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { toast } from "sonner";
import { formatDateShort } from "@/shared/format";
import { CheckCircle2, XCircle, Filter, Camera, Mic, PenLine, ListChecks, PartyPopper } from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";

const SRC_ICONS = { photo: Camera, voice: Mic, manual: PenLine };

export default function Approval() {
  const { jobs } = useApp();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/entries", { params: { status: "pending" } });
      setItems(data);
      setSelected(new Set());
    } catch (err) {
      if (err?.response?.status !== 401) toast.error("Couldn't load the queue");
      setItems([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected((s) => {
      const c = new Set(s);
      c.has(id) ? c.delete(id) : c.add(id);
      return c;
    });
  };
  const selectAll = () => setSelected(new Set(items.map((i) => i.id)));

  const approveOne = async (id) => {
    try { await api.put(`/entries/${id}`, { status: "approved" }); toast.success("Approved"); load(); }
    catch { toast.error("Failed"); }
  };
  const rejectOne = async (id) => {
    try { await api.put(`/entries/${id}`, { status: "rejected" }); toast("Rejected"); load(); }
    catch { toast.error("Failed"); }
  };
  const bulkApprove = async () => {
    if (selected.size === 0) return;
    try {
      await api.post("/entries/bulk-approve", Array.from(selected));
      toast.success(`Approved ${selected.size} entries`);
      load();
    } catch { toast.error("Bulk failed"); }
  };

  const jobName = (id) => jobs.find((j) => j.id === id)?.name || "Unassigned";

  return (
    <div className="space-y-5">
      <PageHeader
        icon={ListChecks}
        title="Approval Queue"
        subtitle={`${items.length} pending ${items.length === 1 ? "entry" : "entries"}`}
        testid="approval-header"
      />

      {items.length > 0 && (
        <div className="sticky top-[64px] z-10 -mx-4 px-4 py-2 bg-[#1F1F1F]/80 backdrop-blur-md border-b border-white/5 flex items-center gap-2">
          <button
            data-testid="approval-select-all"
            onClick={selectAll}
            className="h-11 px-4 rounded-xl bi-ghost-btn text-xs uppercase tracking-wider font-heading font-bold"
          >
            Select all
          </button>
          <button
            data-testid="approval-bulk-approve"
            onClick={bulkApprove}
            disabled={selected.size === 0}
            className="h-11 px-4 rounded-xl bi-accent-btn text-xs uppercase tracking-wider font-heading font-bold disabled:opacity-40 flex items-center gap-1.5 flex-1 justify-center"
          >
            <CheckCircle2 className="w-4 h-4" /> Approve ({selected.size})
          </button>
        </div>
      )}

      {loading && (
        <div className="bi-surface p-10 text-center">
          <div className="text-white/50 inline-flex items-center gap-2">
            <Filter className="w-4 h-4 animate-pulse" /> Loading queue…
          </div>
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="bi-elevate p-10 text-center">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-[#34D399]/15 text-[#34D399] items-center justify-center mb-3">
            <PartyPopper className="w-8 h-8" />
          </div>
          <div className="font-heading text-2xl font-extrabold">You&apos;re all caught up.</div>
          <div className="text-white/60 text-sm mt-1">No pending items to review.</div>
          <Link
            to="/"
            data-testid="approval-empty-cta"
            className="inline-flex mt-5 h-12 px-6 items-center gap-2 rounded-xl bi-accent-btn font-heading text-sm uppercase tracking-wide"
          >
            <Camera className="w-4 h-4" /> Capture something new
          </Link>
        </div>
      )}

      <div className="space-y-2.5">
        {items.map((e) => {
          const Icon = SRC_ICONS[e.source] || PenLine;
          const isSel = selected.has(e.id);
          return (
            <div
              key={e.id}
              data-testid={`approval-item-${e.id}`}
              className={`bi-elevate p-3.5 flex gap-3 transition ${
                isSel ? "ring-2 ring-[#FF9500]/40 border-[#FF9500]/40" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={isSel}
                onChange={() => toggle(e.id)}
                className="w-5 h-5 mt-2.5 accent-[#FF9500]"
                data-testid={`approval-check-${e.id}`}
              />
              <div className="w-11 h-11 rounded-xl bg-[#FF9500]/15 text-[#FF9500] flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-semibold truncate text-[15px]">{e.supplier || "Unknown supplier"}</div>
                  <div className="font-heading text-xl font-extrabold text-[#FF9500] tabular-nums">
                    ${Number(e.total || 0).toFixed(2)}
                  </div>
                </div>
                <div className="text-xs text-white/60 truncate">{e.description || "—"}</div>
                <div className="text-[11px] text-white/40 mt-1 flex items-center gap-1.5 flex-wrap">
                  <span>{formatDateShort(e.entry_date || "")}</span>
                  <span className="text-white/20">·</span>
                  <span>GST ${Number(e.gst || 0).toFixed(2)}</span>
                  <span className="text-white/20">·</span>
                  <span>{jobName(e.job_id)}</span>
                  <span className="text-white/20">·</span>
                  <span className="uppercase tracking-wider text-white/50 font-heading">{e.deduction_category}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    data-testid={`approval-approve-${e.id}`}
                    onClick={() => approveOne(e.id)}
                    className="h-11 px-4 rounded-xl bi-accent-btn text-xs font-heading font-bold uppercase tracking-wider flex items-center gap-1.5 flex-1 justify-center"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Approve
                  </button>
                  <button
                    data-testid={`approval-reject-${e.id}`}
                    onClick={() => rejectOne(e.id)}
                    className="h-11 px-4 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider text-[#F87171] flex items-center gap-1.5"
                  >
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
